import type { EmotionLabel } from '../types';

const positions: Partial<Record<EmotionLabel, [number, number]>> = {
  joy: [1.45, 0.65],
  gratitude: [1.35, 0.25],
  relief: [1.15, -0.25],
  tenderness: [1.2, -0.55],
  hope: [1.15, 0.85],
  pride: [1.35, 1.05],
  anger: [-1.45, 1.25],
  fear: [-1.35, 1.55],
  anxiety: [-1.0, 1.25],
  contempt: [-1.2, 0.45],
  shame: [-1.4, -0.2],
  guilt: [-1.05, -0.45],
  envy: [-0.9, 0.65],
  sadness: [-1.35, -1.05],
  loneliness: [-1.2, -1.35],
  embarrassment: [-0.95, 0.75],
  disappointment: [-1.25, -0.75]
};

function mapPoint(v: number, a: number): [number, number] {
  const x = 150 + v * 56;
  const y = 150 - a * 56;
  return [x, y];
}

export function EmotionWheel({
  labels,
  selected,
  target,
  guess,
  onSelect
}: {
  labels: EmotionLabel[];
  selected?: EmotionLabel | '';
  target?: { valence: number; arousal: number; label: EmotionLabel };
  guess?: { valence: number; arousal: number };
  onSelect?: (label: EmotionLabel) => void;
}) {
  return (
    <svg className="emotion-wheel" viewBox="0 0 300 300" role="img" aria-label="Emotion circumplex map">
      <defs>
        <radialGradient id="wheelGlow" cx="50%" cy="50%" r="60%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.18)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.02)" />
        </radialGradient>
      </defs>
      <circle cx="150" cy="150" r="142" fill="url(#wheelGlow)" />
      <circle cx="150" cy="150" r="112" fill="none" stroke="rgba(255,255,255,.15)" />
      <circle cx="150" cy="150" r="56" fill="none" stroke="rgba(255,255,255,.10)" />
      <line x1="14" y1="150" x2="286" y2="150" stroke="rgba(255,255,255,.18)" />
      <line x1="150" y1="14" x2="150" y2="286" stroke="rgba(255,255,255,.18)" />
      <text x="236" y="142" className="axis">pleasant</text>
      <text x="18" y="142" className="axis">unpleasant</text>
      <text x="158" y="28" className="axis">activated</text>
      <text x="158" y="282" className="axis">quiet</text>
      {labels.map((label) => {
        const [vx, ay] = positions[label] || [0, 0];
        const [x, y] = mapPoint(vx, ay);
        const active = selected === label;
        return (
          <g
            key={label}
            className={`emotion-dot ${active ? 'active' : ''}`}
            tabIndex={onSelect ? 0 : -1}
            role={onSelect ? 'button' : 'presentation'}
            onClick={() => onSelect?.(label)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') onSelect?.(label);
            }}
          >
            <circle cx={x} cy={y} r={active ? 9 : 6} />
            <text x={x + 9} y={y + 4}>{label}</text>
          </g>
        );
      })}
      {guess ? (
        <g className="guess-point">
          <circle cx={mapPoint(guess.valence, guess.arousal)[0]} cy={mapPoint(guess.valence, guess.arousal)[1]} r="9" />
          <text x={mapPoint(guess.valence, guess.arousal)[0] + 12} y={mapPoint(guess.valence, guess.arousal)[1] - 10}>you</text>
        </g>
      ) : null}
      {target ? (
        <g className="target-point">
          <circle cx={mapPoint(target.valence, target.arousal)[0]} cy={mapPoint(target.valence, target.arousal)[1]} r="10" />
          <text x={mapPoint(target.valence, target.arousal)[0] + 12} y={mapPoint(target.valence, target.arousal)[1] + 4}>target</text>
        </g>
      ) : null}
    </svg>
  );
}
